import React, { useState, useEffect } from 'react';
import './DashboardMetrics.css';
import { useMetrics } from '../../hooks/useMetrics';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays, parseISO, eachDayOfInterval, startOfDay } from 'date-fns';
import * as XLSX from 'xlsx';

const DashboardMetrics = () => {
  const { metrics, isLoading, error, refetch: refetchMetrics } = useMetrics();
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [loadingFlagged, setLoadingFlagged] = useState(false);
  const [unflagging, setUnflagging] = useState(new Set());
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deleting, setDeleting] = useState(new Set());
  const [userFilter, setUserFilter] = useState('all'); // 'all', 'empresas', 'profesionales'
  const [profileFilter, setProfileFilter] = useState('all'); // 'all', 'withProfile', 'withoutProfile'
  const [positionFilter, setPositionFilter] = useState('all'); // 'all' or specific employee position (profession)
  const [professionalsByPosition, setProfessionalsByPosition] = useState([]);
  const [loadingProfessionalsByPosition, setLoadingProfessionalsByPosition] = useState(false);
  const [positionStatusFilter, setPositionStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [jobOffersByPosition, setJobOffersByPosition] = useState([]);
  const [loadingJobOffersByPosition, setLoadingJobOffersByPosition] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('registeredCompanies');
  const [timeline, setTimeline] = useState(7); // days
  const [historicalData, setHistoricalData] = useState([]);
  const [applicationsByDayData, setApplicationsByDayData] = useState([]); // from API: daily counts by submission date (new vs old)
  const [countsByDayData, setCountsByDayData] = useState([]); // from API: cumulative counts per day for chart
  const [applicationChartMode, setApplicationChartMode] = useState('daily'); // 'daily' | 'cumulative' for application metrics
  const [dashboardTab, setDashboardTab] = useState('business'); // 'business' | 'engineering'
  const [triggeringJob, setTriggeringJob] = useState(null); // jobName when running
  const [selectedJobDetail, setSelectedJobDetail] = useState(null); // { jobName, label, run } for detail panel
  const [applicationsModalOpen, setApplicationsModalOpen] = useState(false);
  const [jobsWithApplications, setJobsWithApplications] = useState([]);
  const [loadingJobsWithApplications, setLoadingJobsWithApplications] = useState(false);
  const [applicationsModalError, setApplicationsModalError] = useState(null);

  // Close job detail modal on Escape
  useEffect(() => {
    if (!selectedJobDetail) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelectedJobDetail(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedJobDetail]);

  // Close applications modal on Escape
  useEffect(() => {
    if (!applicationsModalOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setApplicationsModalOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [applicationsModalOpen]);

  // Fetch professionals by position and job offers by position when on business tab
  useEffect(() => {
    if (dashboardTab === 'business') {
      fetchProfessionalsByPosition();
      fetchJobOffersByPosition();
    }
  }, [dashboardTab, positionStatusFilter]);

  // Fetch jobs with applications when modal opens
  const fetchJobsWithApplications = async () => {
    setLoadingJobsWithApplications(true);
    setApplicationsModalError(null);
    try {
      const url = `${process.env.REACT_APP_BACKEND_STAGING_URL || ''}/api/admin/metrics/jobs-with-applications`;
      const res = await fetch(url, { credentials: 'include' });
      const raw = await res.json().catch(() => null);
      const data = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.data) ? raw.data : []);
      setJobsWithApplications(data);
      if (!res.ok) setApplicationsModalError(res.status === 401 ? 'Sesión requerida' : `Error ${res.status}`);
    } catch (e) {
      setJobsWithApplications([]);
      setApplicationsModalError('Error de red');
    } finally {
      setLoadingJobsWithApplications(false);
    }
  };

  // Labels for cron jobs (table + detail panel)
  const CRON_JOB_LABELS = {
    'daily-job-lifecycle': 'Ciclo de vida de ofertas (9:00)',
    'scheduled-call-reminders': 'Recordatorios de llamadas (cada 5 min)',
    'hiring-expiration': 'Contratos vencidos + revisión (9:00)',
    'profile-reminder': 'Recordatorio de perfil (10:00)',
    'inactive-no-profile': 'Inactivos sin perfil (10:30)',
    'profile-ready-no-application': 'Perfil listo sin postulación (11:00)',
    'no-applicants': 'Ofertas sin postulantes 48h+ (11:30)',
    'applied-once-reengagement': 'Re-engagement 1 postulación (12:00)',
    'weekly-digest': 'Resumen semanal (lunes 9:00)',
  };

  // Map jobName -> POST path for manual trigger (admin only)
  const cronTriggerEndpoints = {
    'daily-job-lifecycle': '/api/admin/trigger/daily-job-lifecycle',
    'scheduled-call-reminders': '/api/admin/trigger/scheduled-call-reminders',
    'hiring-expiration': '/api/admin/trigger/hiring-expiration',
    'profile-reminder': '/api/admin/trigger/profile-reminder',
    'inactive-no-profile': '/api/admin/send-inactive-no-profile',
    'profile-ready-no-application': '/api/admin/send-profile-ready-no-application',
    'no-applicants': '/api/admin/notify-no-applicants',
    'applied-once-reengagement': '/api/admin/send-applied-once-reengagement',
    'weekly-digest': '/api/admin/send-weekly-digest',
  };

  const triggerCronJob = async (jobName) => {
    const path = cronTriggerEndpoints[jobName];
    if (!path) return;
    setTriggeringJob(jobName);
    try {
      const url = `${process.env.REACT_APP_BACKEND_STAGING_URL}${path}`;
      const res = await fetch(url, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        refetchMetrics();
      } else {
        alert(data.message || `Error al ejecutar el job (${res.status})`);
      }
    } catch (err) {
      alert('Error de red al ejecutar el job');
    } finally {
      setTriggeringJob(null);
    }
  };

  // Fetch flagged users
  const fetchFlaggedUsers = async () => {
    setLoadingFlagged(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/admin/flagged-users`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setFlaggedUsers(data.flaggedUsers || []);
      } else {
        console.warn('Error fetching flagged users, using empty list:', response.statusText);
        setFlaggedUsers([]); // Fallback to empty array
      }
    } catch (error) {
      console.warn('Error fetching flagged users, using empty list:', error);
      setFlaggedUsers([]); // Fallback to empty array
    } finally {
      setLoadingFlagged(false);
    }
  };

  // Unflag a user
  const handleUnflagUser = async (userId) => {
    setUnflagging(prev => new Set(prev).add(userId));
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/admin/unflag-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      
      if (response.ok) {
        // Remove from flagged users list
        setFlaggedUsers(prev => prev.filter(user => user.id !== userId));
        alert('Usuario desbloqueado exitosamente');
      } else {
        alert('Error al desbloquear usuario');
      }
    } catch (error) {
      console.error('Error unflagging user:', error);
      alert('Error al desbloquear usuario');
    } finally {
      setUnflagging(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const fetchProfessionalsByPosition = async () => {
    setLoadingProfessionalsByPosition(true);
    try {
      const status = positionStatusFilter;
      const url = `${process.env.REACT_APP_BACKEND_STAGING_URL || ''}/api/admin/metrics/professionals-by-position?status=${encodeURIComponent(status)}`;
      const res = await fetch(url, { credentials: 'include' });
      const raw = await res.json().catch(() => null);
      const data = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
      setProfessionalsByPosition(data);
    } catch {
      setProfessionalsByPosition([]);
    } finally {
      setLoadingProfessionalsByPosition(false);
    }
  };

  const fetchJobOffersByPosition = async () => {
    setLoadingJobOffersByPosition(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL || ''}/api/admin/metrics/job-offers-by-position`, { credentials: 'include' });
      const raw = await res.json().catch(() => null);
      const data = Array.isArray(raw) ? raw : (raw?.data && Array.isArray(raw.data) ? raw.data : []);
      setJobOffersByPosition(data);
    } catch {
      setJobOffersByPosition([]);
    } finally {
      setLoadingJobOffersByPosition(false);
    }
  };

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/admin/all-users`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.data?.users || []);
      } else {
        console.warn('Error fetching all users, using empty list:', response.statusText);
        setAllUsers([]); // Fallback to empty array
      }
    } catch (error) {
      console.warn('Error fetching all users, using empty list:', error);
      setAllUsers([]); // Fallback to empty array
    } finally {
      setLoadingUsers(false);
    }
  };

  // Delete a user and all related data
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar al usuario "${userName}" y TODOS sus datos relacionados? Esta acción no se puede deshacer.`)) {
      return;
    }

    setDeleting(prev => new Set(prev).add(userId));
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/admin/delete-user`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      
      if (response.ok) {
        // Remove from all users list
        setAllUsers(prev => prev.filter(user => user.id !== userId));
        // Also remove from flagged users if present
        setFlaggedUsers(prev => prev.filter(user => user.id !== userId));
        alert('Usuario eliminado exitosamente');
      } else {
        const errorData = await response.json();
        alert(`Error al eliminar usuario: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error al eliminar usuario');
    } finally {
      setDeleting(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Export users to Excel
  const exportToExcel = () => {
    // Get filtered users (same logic as table display)
    let filteredUsers = userFilter === 'all' 
      ? allUsers 
      : allUsers.filter(user => user.userType === userFilter);
    
    // Apply profile filter if filtering by profesionales
    if (userFilter === 'profesionales' && profileFilter !== 'all') {
      filteredUsers = filteredUsers.filter(user => {
        if (profileFilter === 'withProfile') {
          return user.hasEmployeeProfile === true;
        } else if (profileFilter === 'withoutProfile') {
          return user.hasEmployeeProfile === false;
        }
        return true;
      });
    }
    // Apply position filter
    if (positionFilter !== 'all' && (userFilter === 'profesionales' || userFilter === 'all')) {
      filteredUsers = filteredUsers.filter(user => {
        if (user.userType !== 'profesionales') return true;
        if (positionFilter === '__unspecified__') return !user.employeePosition;
        return user.employeePosition === positionFilter;
      });
    }

    if (filteredUsers.length === 0) {
      alert('No hay usuarios para exportar');
      return;
    }

    // Prepare data for Excel
    const excelData = filteredUsers.map(user => {
      const row = {
        'ID': user.id,
        'Nombre': user.name || 'N/A',
        'Email': user.email,
        'WhatsApp': user.phoneNumber || 'N/A',
        'Tipo': user.userType,
        'Rol': user.role,
      };

      // Add profession and profile columns only if showing profesionales or all
      if (userFilter === 'profesionales' || userFilter === 'all') {
        row['Profesión'] = user.userType === 'profesionales' ? (user.employeePosition || '—') : 'N/A';
        row['Perfil Creado'] = user.userType === 'profesionales' 
          ? (user.hasEmployeeProfile ? 'Sí' : 'No')
          : 'N/A';
      }

      row['Fecha de Registro'] = user.createdAt 
        ? new Date(user.createdAt).toLocaleDateString('es-CL')
        : 'N/A';
      row['Último Login'] = user.lastLoginAt 
        ? new Date(user.lastLoginAt).toLocaleDateString('es-CL')
        : 'Nunca';

      return row;
    });

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');

    // Generate filename with current date
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    const filterStr = userFilter === 'all' 
      ? 'Todos' 
      : userFilter === 'empresas'
      ? 'Empresas'
      : profileFilter === 'all'
      ? 'Profesionales'
      : profileFilter === 'withProfile'
      ? 'Profesionales_Con_Perfil'
      : 'Profesionales_Sin_Perfil';
    
    const filename = `usuarios_${filterStr}_${dateStr}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
  };

  // Store metrics history in localStorage
  const saveMetricsHistory = (currentMetrics) => {
    try {
      const storageKey = 'metrics_history';
      const now = new Date().toISOString();
      
      const historyEntry = {
        date: now,
        registeredCompanies: currentMetrics?.registeredCompanies ?? 0,
        adminCompanyUsers: currentMetrics?.breakdown?.adminCompanyUsers ?? 0,
        registeredProfessionals: currentMetrics?.registeredProfessionals ?? 0,
        professionalUsers: currentMetrics?.breakdown?.professionalUsers ?? 0,
        publishedOffers: currentMetrics?.publishedOffers ?? 0,
        totalApplications: currentMetrics?.totalApplications ?? 0,
        totalConversations: currentMetrics?.totalConversations ?? 0,
        activeProfessionals: currentMetrics?.breakdown?.activeProfessionals ?? 0,
        activeProfessionalsPercentage: currentMetrics?.breakdown?.activeProfessionalsPercentage ?? 0,
        avgSignupToRestaurantDays: currentMetrics?.breakdown?.avgSignupToRestaurantDays ?? null,
        avgRestaurantToJobDays: currentMetrics?.breakdown?.avgRestaurantToJobDays ?? null,
        jobsWithNoApplicationsAfter48h: currentMetrics?.breakdown?.jobsWithNoApplicationsAfter48h ?? 0,
        workersWhoNeverCameBack: currentMetrics?.breakdown?.workersWhoNeverCameBack ?? 0,
        applicationsFromNewUsers: currentMetrics?.breakdown?.applicationsFromNewUsers ?? 0,
        applicationsFromOldUsers: currentMetrics?.breakdown?.applicationsFromOldUsers ?? 0,
      };

      let history = [];
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          history = JSON.parse(stored);
        }
      } catch (e) {
        console.warn('Error reading metrics history, starting fresh:', e);
        history = [];
      }

      // Add new entry
      history.push(historyEntry);

      // Keep only last 365 days (1 year)
      const oneYearAgo = subDays(new Date(), 365);
      history = history.filter(entry => {
        try {
          const entryDate = parseISO(entry.date);
          return entryDate >= oneYearAgo;
        } catch {
          return false;
        }
      });

      // Remove duplicates for the same day (keep only latest)
      const dailyData = {};
      history.forEach(entry => {
        try {
          const day = format(parseISO(entry.date), 'yyyy-MM-dd');
          if (!dailyData[day] || parseISO(entry.date) > parseISO(dailyData[day].date)) {
            dailyData[day] = entry;
          }
        } catch {
          // Skip invalid entries
        }
      });
      history = Object.values(dailyData).sort((a, b) => 
        parseISO(a.date) - parseISO(b.date)
      );

      try {
        localStorage.setItem(storageKey, JSON.stringify(history));
      } catch (e) {
        console.warn('Error saving metrics history, continuing without save:', e);
      }

      return history;
    } catch (error) {
      console.warn('Error processing metrics history, returning empty array:', error);
      return [];
    }
  };

  const isApplicationsByRecencyMetric =
    selectedMetric === 'applicationsFromNewUsers' ||
    selectedMetric === 'applicationsFromOldUsers' ||
    selectedMetric === 'applicationsNewVsOld';
  const isApplicationsByDayMetric =
    selectedMetric === 'totalApplications' ||
    isApplicationsByRecencyMetric;
  const countsByDayMetrics = [
    'registeredCompanies',
    'registeredProfessionals',
    'professionalUsers',
    'publishedOffers',
    'totalConversations'
  ];
  const isCountsByDayMetric = countsByDayMetrics.includes(selectedMetric);

  // Fetch daily application counts by submission date when showing application charts
  useEffect(() => {
    if (!isApplicationsByDayMetric) {
      setApplicationsByDayData([]);
      return;
    }
    let cancelled = false;
    const days = Math.min(Math.max(Number(timeline) || 30, 1), 365);
    fetch(
      `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/admin/metrics/applications-by-day?days=${days}`,
      { credentials: 'include' }
    )
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setApplicationsByDayData(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setApplicationsByDayData([]);
      });
    return () => { cancelled = true; };
  }, [isApplicationsByDayMetric, timeline]);

  // Fetch cumulative counts by day when showing non-application time-series metrics
  useEffect(() => {
    if (!isCountsByDayMetric) {
      setCountsByDayData([]);
      return;
    }
    let cancelled = false;
    const days = Math.min(Math.max(Number(timeline) || 30, 1), 365);
    fetch(
      `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/admin/metrics/counts-by-day?days=${days}`,
      { credentials: 'include' }
    )
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setCountsByDayData(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCountsByDayData([]);
      });
    return () => { cancelled = true; };
  }, [isCountsByDayMetric, timeline]);

  // Load and filter historical data based on timeline
  useEffect(() => {
    const cutoffDate = startOfDay(subDays(new Date(), timeline));
    const today = startOfDay(new Date());
    const allDays = eachDayOfInterval({ start: cutoffDate, end: today });

    // Use API cumulative counts-by-day for companies, professionals, offers, applications, conversations
    if (isCountsByDayMetric && countsByDayData.length > 0) {
      const byDate = new Map(countsByDayData.map((d) => [d.date, d]));
      let lastValue = 0;
      const completeData = allDays.map((day) => {
        const dayKey = format(day, 'yyyy-MM-dd');
        const row = byDate.get(dayKey);
        const value = row != null && row[selectedMetric] != null ? Number(row[selectedMetric]) : lastValue;
        if (row != null && row[selectedMetric] != null) lastValue = value;
        return {
          date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
          fullDate: dayKey,
          value
        };
      });
      setHistoricalData(completeData);
      return;
    }

    // Use API daily counts (by submission date) for total or new/old application metrics
    if (isApplicationsByDayMetric && applicationsByDayData.length >= 0) {
      const byDate = new Map(
        applicationsByDayData.map((d) => [
          d.date,
          {
            totalApplications: d.totalApplications ?? 0,
            applicationsFromNewUsers: d.applicationsFromNewUsers ?? 0,
            applicationsFromOldUsers: d.applicationsFromOldUsers ?? 0
          }
        ])
      );
      const toCumulative = (arr, valueKeyOrFn) => {
        const getVal = typeof valueKeyOrFn === 'function' ? valueKeyOrFn : (item) => item[valueKeyOrFn] ?? 0;
        let sum = 0;
        return arr.map((item) => {
          sum += getVal(item);
          return { ...item, ...(typeof valueKeyOrFn === 'function' ? { value: sum } : { [valueKeyOrFn]: sum }) };
        });
      };

      if (selectedMetric === 'totalApplications') {
        let completeData = allDays.map((day) => {
          const dayKey = format(day, 'yyyy-MM-dd');
          const d = byDate.get(dayKey);
          const value = d ? d.totalApplications : 0;
          return {
            date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
            fullDate: dayKey,
            value
          };
        });
        if (applicationChartMode === 'cumulative') {
          completeData = toCumulative(completeData, (item) => item.value);
        }
        setHistoricalData(completeData);
        return;
      }
      if (selectedMetric === 'applicationsNewVsOld') {
        let completeData = allDays.map((day) => {
          const dayKey = format(day, 'yyyy-MM-dd');
          const d = byDate.get(dayKey) || { applicationsFromNewUsers: 0, applicationsFromOldUsers: 0 };
          return {
            date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
            fullDate: dayKey,
            applicationsFromNewUsers: d.applicationsFromNewUsers,
            applicationsFromOldUsers: d.applicationsFromOldUsers
          };
        });
        if (applicationChartMode === 'cumulative') {
          let sumNew = 0;
          let sumOld = 0;
          completeData = completeData.map((item) => {
            sumNew += item.applicationsFromNewUsers ?? 0;
            sumOld += item.applicationsFromOldUsers ?? 0;
            return { ...item, applicationsFromNewUsers: sumNew, applicationsFromOldUsers: sumOld };
          });
        }
        setHistoricalData(completeData);
      } else {
        const valueKey = selectedMetric === 'applicationsFromNewUsers' ? 'applicationsFromNewUsers' : 'applicationsFromOldUsers';
        let completeData = allDays.map((day) => {
          const dayKey = format(day, 'yyyy-MM-dd');
          const d = byDate.get(dayKey);
          const value = d ? d[valueKey] : 0;
          return {
            date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
            fullDate: dayKey,
            value
          };
        });
        if (applicationChartMode === 'cumulative') {
          completeData = toCumulative(completeData, (item) => item.value);
        }
        setHistoricalData(completeData);
      }
      return;
    }

    if (!metrics) {
      setHistoricalData([]);
      return;
    }

    try {
      const history = saveMetricsHistory(metrics);
      const isDualApplications = selectedMetric === 'applicationsNewVsOld';

      // Create a map of existing data by date
      const dataMap = new Map();
      history
        .filter(entry => {
          try {
            const entryDate = startOfDay(parseISO(entry.date));
            return entryDate >= cutoffDate && entryDate <= today;
          } catch {
            return false;
          }
        })
        .forEach(entry => {
          try {
            const entryDate = startOfDay(parseISO(entry.date));
            const dayKey = format(entryDate, 'yyyy-MM-dd');
            if (isDualApplications) {
              const newVal = entry.applicationsFromNewUsers ?? 0;
              const oldVal = entry.applicationsFromOldUsers ?? 0;
              if (!dataMap.has(dayKey) || parseISO(entry.date) > parseISO(dataMap.get(dayKey).fullDate)) {
                dataMap.set(dayKey, {
                  date: entryDate,
                  fullDate: entry.date,
                  applicationsFromNewUsers: newVal,
                  applicationsFromOldUsers: oldVal
                });
              }
            } else {
              const value = selectedMetric === 'activeProfessionalsPercentage'
                ? (entry.activeProfessionalsPercentage || 0)
                : (entry[selectedMetric] !== null && entry[selectedMetric] !== undefined
                    ? entry[selectedMetric]
                    : 0);
              if (!dataMap.has(dayKey) || parseISO(entry.date) > parseISO(dataMap.get(dayKey).fullDate)) {
                dataMap.set(dayKey, {
                  date: entryDate,
                  fullDate: entry.date,
                  value: value
                });
              }
            }
          } catch {
            // Skip invalid entries
          }
        });

      let completeData;
      if (isDualApplications) {
        let lastNew = 0;
        let lastOld = 0;
        completeData = allDays.map(day => {
          const dayKey = format(day, 'yyyy-MM-dd');
          if (dataMap.has(dayKey)) {
            const d = dataMap.get(dayKey);
            lastNew = d.applicationsFromNewUsers;
            lastOld = d.applicationsFromOldUsers;
            return {
              date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
              fullDate: format(day, 'yyyy-MM-dd'),
              applicationsFromNewUsers: lastNew,
              applicationsFromOldUsers: lastOld
            };
          }
          return {
            date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
            fullDate: format(day, 'yyyy-MM-dd'),
            applicationsFromNewUsers: lastNew,
            applicationsFromOldUsers: lastOld
          };
        });
      } else {
        // Use earliest known value so days without history don't show 0 and then a spike
        const sortedKeys = [...dataMap.keys()].sort();
        const firstValue = sortedKeys.length > 0 ? dataMap.get(sortedKeys[0]).value : 0;
        let lastValue = firstValue;
        completeData = allDays.map(day => {
          const dayKey = format(day, 'yyyy-MM-dd');
          if (dataMap.has(dayKey)) {
            lastValue = dataMap.get(dayKey).value;
            return {
              date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
              fullDate: format(day, 'yyyy-MM-dd'),
              value: lastValue
            };
          }
          return {
            date: format(day, timeline > 90 ? 'MMM dd, yyyy' : 'MMM dd'),
            fullDate: format(day, 'yyyy-MM-dd'),
            value: lastValue
          };
        });
      }

      setHistoricalData(completeData);
    } catch (error) {
      console.warn('Error processing historical data, using empty array:', error);
      setHistoricalData([]);
    }
  }, [metrics, selectedMetric, timeline, applicationsByDayData, countsByDayData, isApplicationsByRecencyMetric, isApplicationsByDayMetric, isCountsByDayMetric, applicationChartMode]);

  // Calculate Y-axis domain and ticks based on data
  const getYAxisConfig = () => {
    if (selectedMetric === 'activeProfessionalsPercentage') {
      return { domain: [0, 100], ticks: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] };
    }
    
    if (historicalData.length === 0) {
      return { domain: [0, 100], ticks: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] };
    }

    const maxValue = selectedMetric === 'applicationsNewVsOld'
      ? Math.max(
          ...historicalData.flatMap(d => [d.applicationsFromNewUsers ?? 0, d.applicationsFromOldUsers ?? 0]),
          0
        )
      : Math.max(...historicalData.map(d => d.value), 0);
    
    // Add significant padding at the top (50% + minimum 50 units) for better visual spacing
    const paddingPercentage = 0.5; // 50% padding
    const minPadding = 50; // Minimum 50 units of padding
    const percentagePadding = maxValue * paddingPercentage;
    const totalPadding = Math.max(percentagePadding, minPadding);
    const paddedMax = maxValue + totalPadding;
    
    // Round up to nearest 50 for cleaner scale, with minimum of 100
    const maxDomain = Math.max(100, Math.ceil(paddedMax / 50) * 50);
    
    // Generate ticks in increments of 20 for better readability
    const ticks = [];
    for (let i = 0; i <= maxDomain; i += 20) {
      ticks.push(i);
    }
    
    return { domain: [0, maxDomain], ticks };
  };

  const yAxisConfig = getYAxisConfig();

  // Load flagged users and all users on component mount
  useEffect(() => {
    fetchFlaggedUsers();
    fetchAllUsers();
  }, []);

  // Always use fallback values if metrics are not available
  const metricsData = [
    {
      title: "Perfiles Empresas",
      value: metrics?.registeredCompanies ?? 0,
      tooltip: "Total de perfiles de empresas registradas (restaurantes + usuarios admin)"
    },
    {
      title: "Usuarios Empresa (Rol Admin)",
      value: metrics?.breakdown?.adminCompanyUsers ?? 0,
      tooltip: "Número de usuarios con rol 'admin' que pertenecen a empresas"
    },
    {
      title: "Perfiles Profesionales",
      value: metrics?.registeredProfessionals ?? 0,
      tooltip: "Usuarios profesionales que completaron su perfil (crearon su perfil de Employee)"
    },
    {
      title: "Usuarios Profesionales",
      value: metrics?.breakdown?.professionalUsers ?? 0,
      tooltip: "Total de usuarios registrados como profesionales (incluye los que aún no completaron su perfil)"
    },
    {
      title: "Ofertas Publicadas",
      value: metrics?.publishedOffers ?? 0,
      tooltip: "Total de ofertas de trabajo publicadas en la plataforma"
    },
    {
      title: "Postulaciones Realizadas",
      value: metrics?.totalApplications ?? 0,
      tooltip: "Total de postulaciones realizadas por profesionales a ofertas de trabajo"
    },
    {
      title: "Postulaciones de usuarios nuevos (≤30 días)",
      value: metrics?.breakdown?.applicationsFromNewUsers ?? 0,
      tooltip: "Postulaciones hechas por profesionales que se registraron en los últimos 30 días. Sirve para ver si la adquisición de nuevos usuarios está generando aplicaciones."
    },
    {
      title: "Postulaciones de usuarios antiguos (>30 días)",
      value: metrics?.breakdown?.applicationsFromOldUsers ?? 0,
      tooltip: "Postulaciones hechas por profesionales que se registraron hace más de 30 días. Sirve para ver si los usuarios ya existentes siguen activos o hay abandono (churn)."
    },
    {
      title: "Conversaciones",
      value: metrics?.totalConversations ?? 0,
      tooltip: "Total de conversaciones entre usuarios y empresas en la plataforma"
    },
    {
      title: "Profesionales Activos",
      value: metrics?.breakdown?.activeProfessionals ?? 0,
      tooltip: "Profesionales con más de 1 postulación en los últimos 14 días",
      percentage: metrics?.breakdown?.activeProfessionalsPercentage ?? null
    },
    {
      title: "Tiempo Promedio(Profesionales): Registro → Perfil",
      value: metrics?.breakdown?.avgSignupToProfileDays ?? null,
      tooltip: "Días promedio desde que un usuario profesional se registra hasta que completa su perfil",
      unit: "días",
      showNull: true
    },
    {
      title: "Tiempo Promedio: Perfil → Primera Postulación",
      value: metrics?.breakdown?.avgProfileToApplicationDays ?? null,
      tooltip: "Días promedio desde que un profesional completa su perfil hasta que aplica a su primer trabajo",
      unit: "días",
      showNull: true
    },
    {
      title: "Tiempo Promedio (Empresas): Registro → Restaurante",
      value: metrics?.breakdown?.avgSignupToRestaurantDays ?? null,
      tooltip: "Días promedio desde que un usuario empresa se registra hasta que crea su restaurante",
      unit: "días",
      showNull: true
    },
    {
      title: "Tiempo Promedio (Empresas): Restaurante → Primer Trabajo",
      value: metrics?.breakdown?.avgRestaurantToJobDays ?? null,
      tooltip: "Días promedio desde que una empresa crea su restaurante hasta que publica su primer trabajo",
      unit: "días",
      showNull: true
    },
    {
      title: "Ofertas Sin Postulaciones (48h+)",
      value: metrics?.breakdown?.jobsWithNoApplicationsAfter48h ?? 0,
      tooltip: "Ofertas de trabajo creadas hace más de 48 horas que aún no tienen postulaciones. Requiere atención urgente para evitar abandono de restaurantes.",
      urgent: true
    },
    {
      title: "Trabajadores que No Regresaron",
      value: metrics?.breakdown?.workersWhoNeverCameBack ?? 0,
      tooltip: "Usuarios profesionales que se registraron pero nunca completaron su perfil. Oportunidad de re-engagement.",
      urgent: true
    }
  ];

  return (
    <>
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard</h1>
        <div className="dashboard-tabs">
          <button
            type="button"
            className={`dashboard-tab ${dashboardTab === 'business' ? 'active' : ''}`}
            onClick={() => setDashboardTab('business')}
          >
            Business
          </button>
          <button
            type="button"
            className={`dashboard-tab ${dashboardTab === 'engineering' ? 'active' : ''}`}
            onClick={() => setDashboardTab('engineering')}
          >
            Engineering
          </button>
        </div>
      </div>

      {dashboardTab === 'business' && (
        <>
      {isLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando métricas...</div>
      ) : (
        <div className="dashboard-metrics">
          {metricsData.map((metric, index) => {
            const displayValue = metric.value === null && metric.showNull 
              ? 'N/A' 
              : metric.percentage !== null && metric.percentage !== undefined
              ? `${metric.value} (${metric.percentage}%)`
              : metric.unit
              ? `${metric.value !== null ? metric.value : 'N/A'} ${metric.unit}`
              : metric.value;
            
            const isApplicationsCard = metric.title === 'Postulaciones Realizadas';
            return (
              <div
                className={`metric-card ${metric.urgent ? 'metric-urgent' : ''} ${isApplicationsCard ? 'metric-card-clickable' : ''}`}
                key={index}
                style={metric.urgent ? {
                  border: '2px solid #ff6b6b',
                  backgroundColor: '#fff5f5'
                } : {}}
                onClick={isApplicationsCard ? () => { setApplicationsModalOpen(true); fetchJobsWithApplications(); } : undefined}
                onKeyDown={isApplicationsCard ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setApplicationsModalOpen(true); fetchJobsWithApplications(); } } : undefined}
                role={isApplicationsCard ? 'button' : undefined}
                tabIndex={isApplicationsCard ? 0 : undefined}
              >
              <div className="metric-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', overflow: 'visible' }}>
                <h3>{metric.title}</h3>
                    {metric.urgent && (
                      <span style={{ 
                        fontSize: '12px',
                        color: '#ff6b6b',
                        fontWeight: 'bold',
                        backgroundColor: '#ffe0e0',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        URGENTE
                      </span>
                    )}
                    {metric.tooltip && (
                      <div 
                        className="metric-tooltip-container"
                        onMouseEnter={(e) => {
                          const icon = e.currentTarget.querySelector('.metric-tooltip-icon');
                          const tooltip = e.currentTarget.querySelector('.metric-tooltip-text');
                          if (icon && tooltip) {
                            const iconRect = icon.getBoundingClientRect();
                            
                            // Position tooltip below icon
                            const top = iconRect.bottom + 12;
                            const left = iconRect.left + (iconRect.width / 2);
                            
                            tooltip.style.top = `${top}px`;
                            tooltip.style.left = `${left}px`;
                            tooltip.style.transform = 'translateX(-50%)';
                            tooltip.style.visibility = 'visible';
                            tooltip.style.opacity = '1';
                          }
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.querySelector('.metric-tooltip-text');
                          if (tooltip) {
                            tooltip.style.visibility = 'hidden';
                            tooltip.style.opacity = '0';
                          }
                        }}
                      >
                        <span className="metric-tooltip-icon">
                          ?
                        </span>
                        <span className="metric-tooltip-text">
                          {metric.tooltip}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="metric-value" style={metric.urgent ? { color: '#ff6b6b', fontWeight: 'bold' } : {}}>
                    {displayValue}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Metrics Chart Section */}
      <div className="metrics-chart-section">
        <h2 className="chart-title">Gráfico de Métricas Temporales</h2>
        
        <div className="chart-controls">
          <div className="control-group">
            <label htmlFor="metric-selector">Métrica:</label>
            <select
              id="metric-selector"
              className="chart-selector"
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
            >
              <option value="registeredCompanies">Empresas Registradas</option>
              <option value="adminCompanyUsers">Usuarios Empresa (Rol Admin)</option>
              <option value="registeredProfessionals">Profesionales Registrados</option>
              <option value="professionalUsers">Usuarios Profesionales</option>
              <option value="publishedOffers">Ofertas Publicadas</option>
              <option value="totalApplications">Postulaciones Realizadas</option>
              <option value="applicationsNewVsOld">Postulaciones: nuevos vs antiguos (gráfico dual)</option>
              <option value="applicationsFromNewUsers">Postulaciones de usuarios nuevos (≤30 días)</option>
              <option value="applicationsFromOldUsers">Postulaciones de usuarios antiguos (&gt;30 días)</option>
              <option value="totalConversations">Conversaciones</option>
              <option value="activeProfessionals">Profesionales Activos</option>
              <option value="activeProfessionalsPercentage">% Profesionales Activos</option>
              <option value="avgSignupToRestaurantDays">Tiempo Promedio: Registro → Restaurante</option>
              <option value="avgRestaurantToJobDays">Tiempo Promedio: Restaurante → Primer Trabajo</option>
              <option value="jobsWithNoApplicationsAfter48h">Ofertas Sin Postulaciones (48h+)</option>
              <option value="workersWhoNeverCameBack">Trabajadores que No Regresaron</option>
            </select>
          </div>
          
          <div className="control-group">
            <label htmlFor="timeline-selector">Período:</label>
            <select
              id="timeline-selector"
              className="chart-selector"
              value={timeline}
              onChange={(e) => setTimeline(Number(e.target.value))}
            >
              <option value={7}>Últimos 7 días</option>
              <option value={15}>Últimos 15 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={60}>Últimos 60 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={120}>Últimos 120 días</option>
              <option value={150}>Últimos 150 días</option>
              <option value={180}>Últimos 6 meses</option>
              <option value={365}>Todo el año</option>
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="application-chart-mode">Vista:</label>
            <select
              id="application-chart-mode"
              className="chart-selector"
              value={applicationChartMode}
              onChange={(e) => setApplicationChartMode(e.target.value)}
            >
              <option value="daily">Por día</option>
              <option value="cumulative">Acumulado</option>
            </select>
          </div>
        </div>

        <div className="chart-container">
          {historicalData.length > 0 ? (
            <ResponsiveContainer width="100%" height={600}>
              <LineChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeOpacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  style={{ fontFamily: '"Source Sans 3", sans-serif', fontSize: '13px' }}
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={(() => {
                    // Show approximately 10-15 labels based on timeline
                    if (timeline <= 7) return 0; // Show all days for 7 days
                    if (timeline <= 15) return Math.floor(historicalData.length / 8); // ~8 labels for 15 days
                    if (timeline <= 30) return Math.floor(historicalData.length / 10); // ~10 labels for 30 days
                    if (timeline <= 90) return Math.floor(historicalData.length / 12); // ~12 labels for 90 days
                    return Math.floor(historicalData.length / 15); // ~15 labels for longer periods
                  })()}
                  tick={{ fill: '#666', fontSize: 12 }}
                  padding={{ left: 10, right: 10 }}
                />
                <YAxis 
                  stroke="#666"
                  style={{ fontFamily: '"Source Sans 3", sans-serif' }}
                  domain={yAxisConfig.domain}
                  allowDecimals={false}
                  tick={{ fill: '#666', fontSize: 12 }}
                  ticks={yAxisConfig.ticks}
                  width={70}
                  padding={{ top: 20, bottom: 20 }}
                  label={{ 
                    value: (() => {
                      const keyMap = {
                        registeredCompanies: 'Empresas Registradas',
                        adminCompanyUsers: 'Usuarios Empresa (Rol Admin)',
                        registeredProfessionals: 'Profesionales Registrados',
                        professionalUsers: 'Usuarios Profesionales',
                        publishedOffers: 'Ofertas Publicadas',
                      totalApplications: 'Postulaciones Realizadas',
                      applicationsNewVsOld: 'Postulaciones',
                      applicationsFromNewUsers: 'Postulaciones (usuarios nuevos)',
                      applicationsFromOldUsers: 'Postulaciones (usuarios antiguos)',
                      totalConversations: 'Conversaciones',
                      activeProfessionals: 'Profesionales Activos',
                      activeProfessionalsPercentage: 'Porcentaje (%)',
                      jobsWithNoApplicationsAfter48h: 'Ofertas Sin Postulaciones (48h+)',
                      workersWhoNeverCameBack: 'Trabajadores que No Regresaron'
                      };
                      const base = keyMap[selectedMetric] || 'Valor';
                      const suffix = applicationChartMode === 'cumulative' ? ' (acumulado)' : '';
                      return base + suffix;
                    })(),
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontFamily: '"Source Sans 3", sans-serif', fill: '#666', fontSize: '14px' }
                  }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    fontFamily: '"Source Sans 3", sans-serif'
                  }}
                />
                <Legend 
                  wrapperStyle={{ fontFamily: '"Source Sans 3", sans-serif' }}
                />
                {selectedMetric === 'applicationsNewVsOld' ? (
                  <>
                    <Line
                      type="monotone"
                      dataKey="applicationsFromNewUsers"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ r: 5, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 8, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                      name={applicationChartMode === 'cumulative' ? 'Usuarios nuevos (≤30 días) — acumulado' : 'Usuarios nuevos (≤30 días)'}
                    />
                    <Line
                      type="monotone"
                      dataKey="applicationsFromOldUsers"
                      stroke="#64748b"
                      strokeWidth={3}
                      dot={{ r: 5, fill: '#64748b', stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 8, fill: '#64748b', stroke: '#fff', strokeWidth: 2 }}
                      name={applicationChartMode === 'cumulative' ? 'Usuarios antiguos (>30 días) — acumulado' : 'Usuarios antiguos (>30 días)'}
                    />
                  </>
                ) : (
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#fb5424" 
                    strokeWidth={3}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      return (
                        <g>
                          <circle
                            cx={cx}
                            cy={cy}
                            r={6}
                            fill="#fb5424"
                            stroke="#fff"
                            strokeWidth={2}
                          />
                          <text
                            x={cx}
                            y={cy - 12}
                            textAnchor="middle"
                            fill="#fb5424"
                            fontSize={11}
                            fontWeight="600"
                            fontFamily='"Source Sans 3", sans-serif'
                          >
                            {payload.value}
                          </text>
                        </g>
                      );
                    }}
                    activeDot={{ 
                      r: 10, 
                      fill: '#fb5424',
                      stroke: '#fff',
                      strokeWidth: 2
                    }}
                    name={(() => {
                      const keyMap = {
                        registeredCompanies: 'Empresas Registradas',
                        adminCompanyUsers: 'Usuarios Empresa (Rol Admin)',
                        registeredProfessionals: 'Profesionales Registrados',
                        professionalUsers: 'Usuarios Profesionales',
                        publishedOffers: 'Ofertas Publicadas',
                        totalApplications: 'Postulaciones Realizadas',
                        applicationsNewVsOld: 'Postulaciones',
                        applicationsFromNewUsers: 'Postulaciones (usuarios nuevos)',
                        applicationsFromOldUsers: 'Postulaciones (usuarios antiguos)',
                        totalConversations: 'Conversaciones',
                        activeProfessionals: 'Profesionales Activos',
                        activeProfessionalsPercentage: '% Profesionales Activos',
                        jobsWithNoApplicationsAfter48h: 'Ofertas Sin Postulaciones (48h+)',
                        workersWhoNeverCameBack: 'Trabajadores que No Regresaron'
                      };
                      const base = keyMap[selectedMetric] || 'Métrica';
                      const suffix = applicationChartMode === 'cumulative' ? ' (acumulado)' : '';
                      return base + suffix;
                    })()}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">
              <p>No hay datos históricos disponibles aún. Los datos se comenzarán a registrar a partir de ahora.</p>
            </div>
          )}
        </div>
      </div>

      {/* Professionals by position table - underneath Gráfico de Métricas Temporales */}
      <div className="professionals-by-position-section">
        <h2 className="chart-title">Profesionales por posición</h2>
        <div className="professionals-by-position-controls">
          <label className="professionals-by-position-label">Estado:</label>
          <select
            className="professionals-by-position-select"
            value={positionStatusFilter}
            onChange={(e) => setPositionStatusFilter(e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="active">Activos (&gt;1 postulación en últimos 14 días)</option>
            <option value="inactive">Inactivos (≤1 postulación en últimos 14 días)</option>
          </select>
          {!loadingProfessionalsByPosition && professionalsByPosition.length > 0 && (
            <span className="professionals-by-position-total">
              Total: <strong>{professionalsByPosition.reduce((s, r) => s + r.count, 0)}</strong> profesionales
            </span>
          )}
        </div>
        {loadingProfessionalsByPosition ? (
          <div className="engineering-loading">Cargando...</div>
        ) : professionalsByPosition.length === 0 ? (
          <p className="engineering-detail-empty">No hay datos de profesionales por posición.</p>
        ) : (
          <div className="professionals-by-position-table-wrap">
            <table className="professionals-by-position-table">
              <thead>
                <tr>
                  <th>Posición</th>
                  <th>Nº profesionales ↓</th>
                </tr>
              </thead>
              <tbody>
                {professionalsByPosition.map((row) => (
                  <tr key={row.position}>
                    <td className="professionals-by-position-cell-position">{row.position}</td>
                    <td className="professionals-by-position-cell-count"><strong>{row.count}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Job offers by position table - underneath Profesionales por posición */}
      <div className="professionals-by-position-section job-offers-by-position-section">
        <h2 className="chart-title">Ofertas de trabajo por posición</h2>
        {loadingJobOffersByPosition ? (
          <div className="engineering-loading">Cargando...</div>
        ) : jobOffersByPosition.length === 0 ? (
          <p className="engineering-detail-empty">No hay ofertas por posición.</p>
        ) : (
          <>
            <div className="professionals-by-position-controls">
              <span className="professionals-by-position-total">
                Total: <strong>{jobOffersByPosition.reduce((s, r) => s + r.count, 0)}</strong> ofertas
              </span>
            </div>
            <div className="professionals-by-position-table-wrap">
              <table className="professionals-by-position-table">
                <thead>
                  <tr>
                    <th>Posición</th>
                    <th>Nº ofertas ↓</th>
                  </tr>
                </thead>
                <tbody>
                  {jobOffersByPosition.map((row) => (
                    <tr key={row.position}>
                      <td className="professionals-by-position-cell-position">{row.position}</td>
                      <td className="professionals-by-position-cell-count"><strong>{row.count}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Flagged Users Section */}
      <div className="flagged-users-section">
        <h2 className="flagged-users-title">Usuarios Bloqueados</h2>
        <button 
          className="refresh-flagged-btn" 
          onClick={fetchFlaggedUsers}
          disabled={loadingFlagged}
        >
          {loadingFlagged ? 'Cargando...' : 'Actualizar Lista'}
        </button>
        
        {loadingFlagged ? (
          <div className="loading-flagged">Cargando usuarios bloqueados...</div>
        ) : flaggedUsers.length === 0 ? (
          <div className="no-flagged-users">No hay usuarios bloqueados</div>
        ) : (
          <div className="flagged-users-list">
            {flaggedUsers.map((user) => (
              <div className="flagged-user-card" key={user.id}>
                <div className="user-info">
                  <h4>{user.name || user.email}</h4>
                  <p className="user-email">{user.email}</p>
                  <p className="user-type">Tipo: {user.userType}</p>
                  <p className="lockout-info">
                    Intentos: {user.attempts || 0} | 
                    Bloqueado hasta: {user.lockUntil ? new Date(user.lockUntil).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <button
                  className="unflag-btn"
                  onClick={() => handleUnflagUser(user.id)}
                  disabled={unflagging.has(user.id)}
                >
                  {unflagging.has(user.id) ? 'Desbloqueando...' : 'Desbloquear'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Users Section */}
      <div className="all-users-section">
        <div className="all-users-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            <h2 className="all-users-title">Gestión de Usuarios</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <label style={{ 
                fontFamily: '"Source Sans 3", sans-serif',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#334155'
              }}>
                Filtrar:
              </label>
              <select
                value={userFilter}
                onChange={(e) => {
                  setUserFilter(e.target.value);
                  if (e.target.value !== 'profesionales') {
                    setProfileFilter('all');
                    setPositionFilter('all');
                  }
                }}
                style={{
                  fontFamily: '"Source Sans 3", sans-serif',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  fontSize: '0.9rem',
                  backgroundColor: 'white',
                  color: '#334155',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.borderColor = '#fb5424'}
                onMouseLeave={(e) => e.target.style.borderColor = '#e2e8f0'}
              >
                <option value="all">Todos</option>
                <option value="empresas">Empresas</option>
                <option value="profesionales">Profesionales</option>
              </select>
              {userFilter === 'profesionales' && (
                <select
                  value={profileFilter}
                  onChange={(e) => setProfileFilter(e.target.value)}
                  style={{
                    fontFamily: '"Source Sans 3", sans-serif',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: '2px solid #e2e8f0',
                    fontSize: '0.9rem',
                    backgroundColor: 'white',
                    color: '#334155',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.borderColor = '#fb5424'}
                  onMouseLeave={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="all">Todos los profesionales</option>
                  <option value="withProfile">Con perfil creado</option>
                  <option value="withoutProfile">Sin perfil creado</option>
                </select>
              )}
              {(userFilter === 'profesionales' || userFilter === 'all') && (() => {
                const proUsers = allUsers.filter(u => u.userType === 'profesionales');
                const distinctRaw = [...new Set(proUsers.map(u => u.employeePosition ?? ''))];
                const hasUnspecified = distinctRaw.some(p => !p);
                const positionOptions = distinctRaw.filter(Boolean).sort((a, b) => String(a).localeCompare(String(b)));
                if (proUsers.length === 0) return null;
                return (
                  <select
                    value={positionFilter}
                    onChange={(e) => setPositionFilter(e.target.value)}
                    style={{
                      fontFamily: '"Source Sans 3", sans-serif',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: '2px solid #e2e8f0',
                      fontSize: '0.9rem',
                      backgroundColor: 'white',
                      color: '#334155',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.borderColor = '#fb5424'}
                    onMouseLeave={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="all">Todas las profesiones</option>
                    {positionOptions.map(pos => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                    {hasUnspecified && <option value="__unspecified__">Sin especificar</option>}
                  </select>
                );
              })()}
              <span style={{
                fontFamily: '"Source Sans 3", sans-serif',
                fontSize: '0.9rem',
                color: '#64748b',
                fontWeight: 600
              }}>
                {(() => {
                  let base = userFilter === 'all' ? allUsers : allUsers.filter(u => u.userType === userFilter);
                  if (userFilter === 'profesionales' && profileFilter !== 'all') {
                    base = base.filter(u => profileFilter === 'withProfile' ? u.hasEmployeeProfile === true : u.hasEmployeeProfile === false);
                  }
                  if (positionFilter !== 'all' && (userFilter === 'profesionales' || userFilter === 'all')) {
                    base = base.filter(u => {
                      if (u.userType !== 'profesionales') return true;
                      if (positionFilter === '__unspecified__') return !u.employeePosition;
                      return u.employeePosition === positionFilter;
                    });
                  }
                  const count = base.length;
                  return userFilter === 'all' 
                    ? `Total: ${count}` 
                    : userFilter === 'empresas'
                    ? `Empresas: ${count}`
                    : profileFilter === 'all'
                    ? (positionFilter === 'all' ? `Profesionales: ${count}` : `Profesión: ${count}`)
                    : profileFilter === 'withProfile'
                    ? `Con perfil: ${count}`
                    : `Sin perfil: ${count}`;
                })()}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button 
              className="refresh-users-btn" 
              onClick={fetchAllUsers}
              disabled={loadingUsers}
            >
              {loadingUsers ? 'Cargando...' : 'Actualizar Lista'}
            </button>
            <button 
              onClick={exportToExcel}
              disabled={loadingUsers || allUsers.length === 0}
              style={{
                fontFamily: '"Source Sans 3", sans-serif',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                backgroundColor: '#10b981',
                color: 'white',
                cursor: loadingUsers || allUsers.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: loadingUsers || allUsers.length === 0 ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!loadingUsers && allUsers.length > 0) {
                  e.target.style.backgroundColor = '#059669';
                }
              }}
              onMouseLeave={(e) => {
                if (!loadingUsers && allUsers.length > 0) {
                  e.target.style.backgroundColor = '#10b981';
                }
              }}
            >
              📥 Descargar Excel
            </button>
          </div>
        </div>
        
        {loadingUsers ? (
          <div className="loading-users">Cargando usuarios...</div>
        ) : allUsers.length === 0 ? (
          <div className="no-users">No hay usuarios registrados</div>
        ) : (() => {
          let filteredUsers = userFilter === 'all' 
            ? allUsers 
            : allUsers.filter(user => user.userType === userFilter);
          
          // Apply profile filter if filtering by profesionales
          if (userFilter === 'profesionales' && profileFilter !== 'all') {
            filteredUsers = filteredUsers.filter(user => {
              if (profileFilter === 'withProfile') {
                return user.hasEmployeeProfile === true;
              } else if (profileFilter === 'withoutProfile') {
                return user.hasEmployeeProfile === false;
              }
              return true;
            });
          }
          // Apply position (profession) filter for professionals
          if (positionFilter !== 'all' && (userFilter === 'profesionales' || userFilter === 'all')) {
            filteredUsers = filteredUsers.filter(user => {
              if (user.userType !== 'profesionales') return true;
              if (positionFilter === '__unspecified__') return !user.employeePosition;
              return user.employeePosition === positionFilter;
            });
          }

          return filteredUsers.length === 0 ? (
            <div className="no-users">No hay usuarios de este tipo</div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>WhatsApp</th>
                    <th>Tipo</th>
                    <th>Rol</th>
                    {(userFilter === 'profesionales' || userFilter === 'all') && (
                      <th>Profesión</th>
                    )}
                    {userFilter === 'profesionales' || userFilter === 'all' ? (
                      <th>Perfil Creado</th>
                    ) : null}
                    <th>Registro</th>
                    <th>Último Login</th>
                    <th>Conversaciones</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="user-row">
                      <td>{user.id}</td>
                      <td>{user.name || 'N/A'}</td>
                      <td>{user.email}</td>
                      <td>{user.phoneNumber || 'N/A'}</td>
                      <td>
                        <span className={`user-type-badge ${user.userType}`}>
                          {user.userType}
                        </span>
                      </td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      {(userFilter === 'profesionales' || userFilter === 'all') && (
                        <td>{user.userType === 'profesionales' ? (user.employeePosition || '—') : '—'}</td>
                      )}
                      {(userFilter === 'profesionales' || userFilter === 'all') && (
                        <td>
                          {user.userType === 'profesionales' ? (
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              backgroundColor: user.hasEmployeeProfile ? '#d1fae5' : '#fee2e2',
                              color: user.hasEmployeeProfile ? '#065f46' : '#991b1b'
                            }}>
                              {user.hasEmployeeProfile ? 'Sí' : 'No'}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>N/A</span>
                          )}
                        </td>
                      )}
                      <td>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td>
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Nunca'}
                      </td>
                      <td>{user.conversationCount ?? 0}</td>
                      <td>
                        <button
                          className="delete-user-btn"
                          onClick={() => handleDeleteUser(user.id, user.name || user.email)}
                          disabled={deleting.has(user.id)}
                          title="Eliminar usuario y todos sus datos"
                        >
                          {deleting.has(user.id) ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Modal: Postulaciones Realizadas - jobs with application count and company */}
      {applicationsModalOpen && (
        <div
          className="engineering-modal-backdrop"
          onClick={() => setApplicationsModalOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setApplicationsModalOpen(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="applications-modal-title"
        >
          <div className="engineering-modal-dialog applications-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="engineering-detail-header">
              <h4 id="applications-modal-title" className="engineering-detail-title">Postulaciones por oferta</h4>
              <button type="button" className="engineering-detail-close" onClick={() => setApplicationsModalOpen(false)} aria-label="Cerrar">×</button>
            </div>
            <div className="engineering-detail-body">
              {loadingJobsWithApplications ? (
                <p className="engineering-detail-empty">Cargando...</p>
              ) : applicationsModalError ? (
                <p className="engineering-detail-error">{applicationsModalError}</p>
              ) : jobsWithApplications.length === 0 ? (
                <p className="engineering-detail-empty">No hay ofertas con postulaciones.</p>
              ) : (
                <div className="applications-modal-table-wrap">
                  <table className="engineering-jobs-table">
                    <thead>
                      <tr>
                        <th>Oferta (puesto)</th>
                        <th>Empresa</th>
                        <th>Nº postulaciones ↓</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...jobsWithApplications]
                        .sort((a, b) => (b.applicationCount - a.applicationCount))
                        .map((row) => (
                        <tr key={row.jobId}>
                          <td className="engineering-cell-job">{row.jobTitle}</td>
                          <td>{row.companyName}</td>
                          <td><strong>{row.applicationCount}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {dashboardTab === 'engineering' && (
        <div className="dashboard-engineering">
          <h2 className="engineering-section-title">Jobs programados</h2>
          <p className="engineering-section-desc">Última ejecución de cada job. Usa «Ejecutar ahora» para lanzarlos manualmente.</p>
          {isLoading ? (
            <div className="engineering-loading">Cargando...</div>
          ) : (
            <div className="cron-runs-grid">
              {(() => {
                const jobIds = Object.keys(cronTriggerEndpoints);
                const runsByJob = (metrics?.cronRuns || []).reduce((acc, r) => { acc[r.jobName] = r; return acc; }, {});
                return jobIds.map((jobName) => {
                  const run = runsByJob[jobName];
                  const result = run?.result || {};
                  const success = result.success === true;
                  const label = CRON_JOB_LABELS[jobName] || jobName;
                  const lastRun = run?.lastRunAt
                    ? format(parseISO(run.lastRunAt), 'd MMM yyyy, HH:mm')
                    : '—';
                  let details = '';
                  if (success) {
                    if (result.processedOffers != null) details = `Ofertas: ${result.processedOffers} · Notificados: ${result.totalNotified ?? 0} · Errores: ${result.errorCount ?? 0}`;
                    else if (result.sent != null) details = `Enviados: ${result.sent} · Procesados: ${result.processed ?? 0}`;
                    else if (result.notified != null) details = `Notificados: ${result.notified} · Procesados: ${result.processed ?? 0}`;
                  }
                  const isRunning = triggeringJob === jobName;
                  return (
                    <div key={jobName} className={`cron-run-card ${run ? (success ? '' : 'cron-run-error') : ''}`}>
                      <div className="cron-run-header">
                        <span className="cron-run-name">{label}</span>
                        {run ? (
                          success ? (
                            <span className="cron-run-badge success">OK</span>
                          ) : (
                            <span className="cron-run-badge error">Error</span>
                          )
                        ) : (
                          <span className="cron-run-badge">Sin ejecución</span>
                        )}
                      </div>
                      <div className="cron-run-time">Última ejecución: {lastRun}</div>
                      {success && details && (
                        <div className="cron-run-details">{details}</div>
                      )}
                      {!success && result.error && (
                        <div className="cron-run-error-msg">{result.error}</div>
                      )}
                      <button
                        type="button"
                        className="cron-run-trigger-btn"
                        onClick={() => triggerCronJob(jobName)}
                        disabled={isRunning}
                      >
                        {isRunning ? 'Ejecutando…' : 'Ejecutar ahora'}
                      </button>
                    </div>
                  );
                });
              })()}
            </div>
          )}
          {!isLoading && (
            <>
              <h3 className="engineering-table-title">Tabla de jobs enviados</h3>
              <p className="engineering-section-desc">Haz clic en una fila para ver más detalles (estado de envío, entregas y aperturas desde Brevo).</p>
              <div className="engineering-jobs-table-wrap">
                <table className="engineering-jobs-table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Última ejecución</th>
                      <th>Estado</th>
                      <th>Detalles</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const jobIds = Object.keys(cronTriggerEndpoints);
                      const runsByJob = (metrics?.cronRuns || []).reduce((acc, r) => { acc[r.jobName] = r; return acc; }, {});
                      return jobIds.map((jobName) => {
                        const run = runsByJob[jobName];
                        const result = run?.result || {};
                        const success = result.success === true;
                        const label = CRON_JOB_LABELS[jobName] || jobName;
                        const lastRun = run?.lastRunAt
                          ? format(parseISO(run.lastRunAt), 'd MMM yyyy, HH:mm')
                          : '—';
                        let details = '';
                        if (success) {
                          if (result.processedOffers != null) details = `Ofertas: ${result.processedOffers} · Notificados: ${result.totalNotified ?? 0} · Errores: ${result.errorCount ?? 0}`;
                          else if (result.sent != null) details = `Enviados: ${result.sent} · Procesados: ${result.processed ?? 0}`;
                          else if (result.notified != null) details = `Notificados: ${result.notified} · Procesados: ${result.processed ?? 0}`;
                        } else if (result.error) details = result.error;
                        const statusText = run ? (success ? 'OK' : 'Error') : 'Sin ejecución';
                        const isRunning = triggeringJob === jobName;
                        const isSelected = selectedJobDetail?.jobName === jobName;
                        const openModal = () => setSelectedJobDetail(isSelected ? null : { jobName, label, run: run || null });
                        return (
                          <tr
                            key={jobName}
                            className={`${run ? (success ? '' : 'engineering-row-error') : ''} ${isSelected ? 'engineering-row-selected' : ''} engineering-row-clickable`}
                            onClick={(e) => { if (!e.target.closest('button')) openModal(); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(); } }}
                          >
                            <td className="engineering-cell-job">{label}</td>
                            <td className="engineering-cell-time">{lastRun}</td>
                            <td>
                              <span className={`engineering-badge ${run ? (success ? 'success' : 'error') : 'muted'}`}>
                                {statusText}
                              </span>
                            </td>
                            <td className="engineering-cell-details">{details || '—'}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="cron-run-trigger-btn engineering-table-btn"
                                onClick={(e) => { e.stopPropagation(); triggerCronJob(jobName); }}
                                disabled={isRunning}
                              >
                                {isRunning ? 'Ejecutando…' : 'Ejecutar ahora'}
                              </button>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              {selectedJobDetail && (
                <div
                  className="engineering-modal-backdrop"
                  onClick={() => setSelectedJobDetail(null)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setSelectedJobDetail(null); }}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="engineering-modal-title"
                >
                  <div
                    className="engineering-modal-dialog"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="engineering-detail-header">
                      <h4 id="engineering-modal-title" className="engineering-detail-title">{selectedJobDetail.label}</h4>
                      <button
                        type="button"
                        className="engineering-detail-close"
                        onClick={() => setSelectedJobDetail(null)}
                        aria-label="Cerrar"
                      >
                        ×
                      </button>
                    </div>
                    <div className="engineering-detail-body">
                      {selectedJobDetail.run ? (
                        <>
                          <div className="engineering-detail-grid">
                            <div className="engineering-detail-item">
                              <span className="engineering-detail-label">Última ejecución</span>
                              <span className="engineering-detail-value">
                                {format(parseISO(selectedJobDetail.run.lastRunAt), "d 'de' MMMM yyyy, HH:mm")}
                              </span>
                            </div>
                            <div className="engineering-detail-item">
                              <span className="engineering-detail-label">Estado</span>
                              <span className="engineering-detail-value">
                                <span className={`engineering-badge ${selectedJobDetail.run.result?.success ? 'success' : 'error'}`}>
                                  {selectedJobDetail.run.result?.success ? 'OK' : 'Error'}
                                </span>
                              </span>
                            </div>
                          </div>
                          {selectedJobDetail.run.result && (
                            <div className="engineering-detail-section">
                              <h5 className="engineering-detail-section-title">Resumen de la ejecución</h5>
                              <ul className="engineering-detail-list">
                                {selectedJobDetail.run.result.processedOffers != null && (
                                  <li>Ofertas procesadas: {selectedJobDetail.run.result.processedOffers} · Notificados: {selectedJobDetail.run.result.totalNotified ?? 0} · Errores: {selectedJobDetail.run.result.errorCount ?? 0}</li>
                                )}
                                {selectedJobDetail.run.result.sent != null && (
                                  <li>Enviados: {selectedJobDetail.run.result.sent} · Procesados: {selectedJobDetail.run.result.processed ?? 0}</li>
                                )}
                                {selectedJobDetail.run.result.notified != null && (
                                  <li>Notificados: {selectedJobDetail.run.result.notified} · Procesados: {selectedJobDetail.run.result.processed ?? 0}</li>
                                )}
                                {selectedJobDetail.run.result.success === true && !selectedJobDetail.run.result.processedOffers && selectedJobDetail.run.result.sent == null && selectedJobDetail.run.result.notified == null && (
                                  <li>Ejecutado correctamente (sin métricas de envío)</li>
                                )}
                                {selectedJobDetail.run.result.error && (
                                  <li className="engineering-detail-error">Error: {selectedJobDetail.run.result.error}</li>
                                )}
                              </ul>
                            </div>
                          )}
                          <div className="engineering-detail-section">
                            <h5 className="engineering-detail-section-title">Correos enviados por esta notificación</h5>
                            <p className="engineering-detail-note">
                              Total de envíos en esta ejecución: <strong>
                                {selectedJobDetail.run.result?.sent ?? selectedJobDetail.run.result?.notified ?? selectedJobDetail.run.result?.totalNotified ?? '—'}
                              </strong>
                              {selectedJobDetail.run.brevoStats ? (
                                <> · Entregados (Brevo): <strong>{selectedJobDetail.run.brevoStats.delivered ?? '—'}</strong></>
                              ) : null}
                            </p>
                            <p className="engineering-detail-disclaimer">
                              La lista de direcciones de correo (destinatarios) no se guarda en la plataforma. Para ver cada envío, estado de entrega, apertura y clics por correo, revisa el panel de Brevo (Transactional → estadísticas por tag).
                            </p>
                          </div>
                          {selectedJobDetail.run.brevoStats && (
                            <div className="engineering-detail-section">
                              <h5 className="engineering-detail-section-title">Estadísticas Brevo (últimos 14 días)</h5>
                              <p className="engineering-detail-note">Entregas, aperturas y clics agregados para esta campaña.</p>
                              <ul className="engineering-detail-list engineering-detail-brevo">
                                <li><strong>Enviados (requests):</strong> {selectedJobDetail.run.brevoStats.requests ?? '—'}</li>
                                <li><strong>Entregados:</strong> {selectedJobDetail.run.brevoStats.delivered ?? '—'}</li>
                                <li><strong>Aperturas (total):</strong> {selectedJobDetail.run.brevoStats.opens ?? '—'}</li>
                                <li><strong>Aperturas únicas:</strong> {selectedJobDetail.run.brevoStats.uniqueOpens ?? '—'}</li>
                                <li><strong>Clics (total):</strong> {selectedJobDetail.run.brevoStats.clicks ?? '—'}</li>
                                <li><strong>Clics únicos:</strong> {selectedJobDetail.run.brevoStats.uniqueClicks ?? '—'}</li>
                              </ul>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="engineering-detail-empty">Este job aún no se ha ejecutado. Usa «Ejecutar ahora» en la tabla para lanzarlo manualmente.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default DashboardMetrics;